import { BudgetProductType } from 'types/api/budgets.type';

// Map of productId -> BudgetProductType for O(1) lookups and atomic updates
export type ProductsMap = Map<string, BudgetProductType>;

/**
 * The budget page's products, outside React state: every product / colour
 * row subscribes to its own product (useBudgetProduct), so saving one
 * quantity re-renders that product only instead of the whole list. Updates
 * replace only the changed product object; the others keep their identity.
 */
export type BudgetProductsStore = {
  get: (productId: string) => BudgetProductType | undefined;
  subscribe: (listener: () => void) => () => void;
  setProducts: (products: BudgetProductType[]) => void;
  addProducts: (products: BudgetProductType[]) => void;
  updateColorQuantity: (productId: string, colorId: string, quantity: number) => void;
};

export function createBudgetProductsStore(): BudgetProductsStore {
  let products: ProductsMap = new Map();
  const listeners = new Set<() => void>();

  const commit = (next: ProductsMap) => {
    products = next;
    listeners.forEach((listener) => listener());
  };

  return {
    get: (productId) => products.get(productId),

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    // Replace all products (used for initial load)
    setProducts: (productsList) => {
      commit(new Map(productsList.map((product) => [product.mainProduct.id, product])));
    },

    // Add products to the existing ones (used for infinite scroll)
    addProducts: (productsList) => {
      const next = new Map(products);
      productsList.forEach((product) => next.set(product.mainProduct.id, product));
      commit(next);
    },

    // Update one colour's quantity and the product total
    updateColorQuantity: (productId, colorId, quantity) => {
      const product = products.get(productId);
      if (!product) return;

      const colors = product.colors.map((color) =>
        color.id === colorId ? { ...color, currQuantity: quantity } : color
      );
      const next = new Map(products);
      next.set(productId, {
        mainProduct: {
          ...product.mainProduct,
          currQuantity: colors.reduce((sum, item) => sum + item.currQuantity, 0),
        },
        colors,
      });
      commit(next);
    },
  };
}
